import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Truck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { NumericFormat } from 'react-number-format';
import AccountSelectModal from '../../components/modals/AccountSelectModal';
import OperationAddressBlock from '../../components/operations/OperationAddressBlock';
import { createEmptyOperationAddress } from '../../components/operations/operationAddressUtils';
import { PRICE_CURRENCIES } from '../../utils/operationPricing';
import { formatCurrency } from '../../utils/formatters';
import { getCurrencyMeta } from '../../constants/currencies';
import useAccountStore from '../../store/useAccountStore';
import useAuthStore from '../../store/authStore';
import useOperationStore from '../../store/useOperationStore';

const initialForm = {
  account: '',
  operationType: 'None',
  transportMode: 'None',
  serviceType: 'None',
  surveyorName: '',
  estimatedVolume: 0,
  pricingType: 'PER_M3',
  currency: 'TRY',
  unitPriceMinor: 0,
  totalPriceMinor: 0,
  pickupAddress: createEmptyOperationAddress(),
  deliveryAddress: createEmptyOperationAddress()
};

const operationTypeOptions = [
  { value: 'Export', label: 'Export' },
  { value: 'Import', label: 'Import' },
  { value: 'Inland', label: 'Inland' },
  { value: 'None', label: 'Seçiniz' }
];

const serviceTypeOptionsByOperationType = {
  Export: [
    { value: 'Paketleme', label: 'Paketleme' },
    { value: 'Kapıdan Kapıya', label: 'Kapıdan Kapıya' },
    { value: 'Kapıdan Limana', label: 'Kapıdan Limana' },
    { value: 'Kapıdan Depo', label: 'Kapıdan Depo' },
    { value: 'Gümrük Hizmeti', label: 'Gümrük Hizmeti' },
    { value: 'Depolama Hizmeti', label: 'Depolama Hizmeti' }
  ],
  Import: [
    { value: 'Teslimat', label: 'Teslimat' },
    { value: 'Kapıdan Kapıya', label: 'Kapıdan Kapıya' },
    { value: 'Limandan Kapıya', label: 'Limandan Kapıya' },
    { value: 'Depodan Kapıya', label: 'Depodan Kapıya' },
    { value: 'Gümrük Hizmeti', label: 'Gümrük Hizmeti' },
    { value: 'Depolama Hizmeti', label: 'Depolama Hizmeti' }
  ],
  Inland: [
    { value: 'Şehir İçi', label: 'Şehir İçi' },
    { value: 'Şehirler Arası', label: 'Şehirler Arası' },
    { value: 'Depolama Hizmeti', label: 'Depolama Hizmeti' }
  ]
};

const OperationCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountIdParam = searchParams.get('accountId');

  const { createOperation } = useOperationStore();
  const { fetchAccountById } = useAccountStore();
  const currentUser = useAuthStore((state) => state.user);

  const [formData, setFormData] = useState(initialForm);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isAccountSelectOpen, setIsAccountSelectOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentUserId = currentUser?._id || currentUser?.id || '';

  useEffect(() => {
    if (!accountIdParam) return undefined;

    let active = true;

    fetchAccountById(accountIdParam)
      .then((account) => {
        if (!active || !account) return;
        setSelectedAccount(account);
        setFormData((prev) => ({
          ...prev,
          account: account._id
        }));
      });

    return () => {
      active = false;
    };
  }, [accountIdParam, fetchAccountById]);

  const prefillLoading = Boolean(accountIdParam) && !selectedAccount;

  const selectedServiceTypeOptions = useMemo(() => (
    serviceTypeOptionsByOperationType[formData.operationType] || []
  ), [formData.operationType]);

  const estimatedVolumeNumber = Number(formData.estimatedVolume || 0);
  const totalOffer = formData.pricingType === 'PER_M3'
    ? Number((estimatedVolumeNumber * Number(formData.unitPriceMinor || 0)).toFixed(2))
    : Number(formData.totalPriceMinor || 0);
  const displayPrice = formData.pricingType === 'PER_M3'
    ? Number(formData.unitPriceMinor || 0)
    : Number(formData.totalPriceMinor || 0);
  const selectedCurrencyMeta = getCurrencyMeta(formData.currency || 'TRY');
  const selectedCurrencySymbol = selectedCurrencyMeta.symbol || formData.currency || 'TRY';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'operationType'
        ? {
            serviceType: 'None',
            ...(value === 'None' ? { transportMode: 'None' } : {}),
            ...(value === 'Inland' ? { transportMode: 'Land' } : {})
          }
        : {})
      }));
  };

  const handlePricingTypeChange = (nextType) => {
    setFormData((prev) => {
      if (nextType === prev.pricingType) return prev;

      const volume = Number(prev.estimatedVolume || 0);

      if (nextType === 'PER_M3') {
        return {
          ...prev,
          pricingType: nextType,
          unitPriceMinor: volume > 0 ? Number((Number(prev.totalPriceMinor || 0) / volume).toFixed(2)) : Number(prev.unitPriceMinor || 0)
        };
      }

      return {
        ...prev,
        pricingType: nextType,
        totalPriceMinor: Number((volume * Number(prev.unitPriceMinor || 0)).toFixed(2))
      };
    });
  };

  const handleAddressChange = (field) => (nextAddress) => {
    setFormData((prev) => ({
      ...prev,
      [field]: nextAddress
    }));
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setFormData((prev) => ({
      ...prev,
      account: account._id
    }));
    setIsAccountSelectOpen(false);
  };

  const clearSelectedAccount = () => {
    setSelectedAccount(null);
    setFormData((prev) => ({
      ...prev,
      account: ''
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const result = await createOperation({
      ...formData,
      account: formData.account || null,
      surveyorName: formData.surveyorName || '',
      estimatedVolume: estimatedVolumeNumber,
      measuredM3: estimatedVolumeNumber,
      unitPrice: formData.pricingType === 'PER_M3'
        ? Number(formData.unitPriceMinor || 0)
        : estimatedVolumeNumber > 0
          ? Number((totalOffer / estimatedVolumeNumber).toFixed(2))
          : 0,
      totalPrice: totalOffer,
      currency: formData.currency || 'TRY',
      pricePerM3: formData.pricingType === 'PER_M3'
        ? Number(formData.unitPriceMinor || 0)
        : estimatedVolumeNumber > 0
          ? Number((Number(formData.totalPriceMinor || 0) / estimatedVolumeNumber).toFixed(2))
          : 0,
      pricingAmount: totalOffer,
      priceCurrency: formData.currency || 'TRY',
      estimator: currentUserId || null
    });

    setSaving(false);

    if (result.success) {
      toast.success('Operasyon başlatıldı');
      navigate(`/operations/${result.data._id}`);
      return;
    }

    toast.error(result.message || 'Operasyon oluşturulamadı');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Link
              to="/operations"
              className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-900"
            >
              <ArrowLeft size={16} />
              Operasyon Listesi
            </Link>
            <h1 className="mt-3 text-2xl font-black tracking-tight text-slate-900">Yeni Operasyon</h1>
            <p className="mt-1 text-sm font-bold text-slate-500">
              Müşteri, güzergâh ve operasyon bilgilerini doldurarak yeni operasyon kaydı oluşturun.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsAccountSelectOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black"
          >
            <Plus size={18} />
            {selectedAccount ? 'Müşteri Değiştir' : 'Müşteri Seç'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <section className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Müşteri</p>
                <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">Cari Bilgisi</h2>
              </div>
              <button
                type="button"
                onClick={() => setIsAccountSelectOpen(true)}
                className="rounded-full bg-slate-100 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-slate-200"
              >
                {selectedAccount ? 'Değiştir' : 'Seç'}
              </button>
            </div>

            {selectedAccount ? (
              <div className="mt-4 rounded-[1.5rem] border border-indigo-100 bg-indigo-50/70 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-base font-black text-slate-900">{selectedAccount.name}</p>
                    <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {selectedAccount.code || 'Kodsuz'}
                      {selectedAccount.phone ? ` • ${selectedAccount.phone}` : ''}
                      {selectedAccount.taxNumber ? ` • ${selectedAccount.taxNumber}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedAccount}
                    className="rounded-full bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 transition-colors hover:bg-slate-50"
                  >
                    Temizle
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50/70 p-4">
                <p className="text-sm font-bold text-slate-500">
                  {prefillLoading ? 'Müşteri bilgisi yükleniyor...' : 'Müşteri seçilmedi. Bağımsız operasyon da oluşturabilirsiniz.'}
                </p>
              </div>
            )}

            <p className="mt-4 text-[10px] font-medium text-slate-400">
              Seçim penceresinden mevcut müşteriyi seçebilir veya hızlı müşteri oluşturabilirsiniz.
            </p>
          </section>

          <section className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm lg:col-span-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Operasyon Bilgileri</p>
                <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">Hizmet ve Ölçüm</h2>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <label className="space-y-1">
                <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Operasyon Türü</span>
                <select
                  name="operationType"
                  value={formData.operationType}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                >
                  {operationTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1">
                <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Taşıma Modu</span>
                <select
                  name="transportMode"
                  value={formData.transportMode}
                  onChange={handleChange}
                  disabled={formData.operationType === 'None'}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                >
                  <option value="None">Seçiniz</option>
                  <option value="Air">Air</option>
                  <option value="Sea">Sea</option>
                  <option value="Land">Land</option>
                </select>
              </label>

              <label className="space-y-1 md:col-span-2 xl:col-span-1">
                <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Hizmet Türü</span>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  disabled={formData.operationType === 'None'}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                >
                  {formData.operationType === 'None' ? (
                    <option value="None">Önce Operasyon Türü Seçiniz</option>
                  ) : (
                    <>
                      <option value="None">Seçiniz</option>
                      {selectedServiceTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </>
                  )}
                </select>
                {formData.operationType === 'None' && (
                  <p className="pl-1 text-[10px] font-bold text-slate-400">Önce operasyon türü seçiniz.</p>
                )}
              </label>
            </div>

            <div className="mt-5 space-y-5 border-t border-slate-100 pt-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-1">
                  <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Keşif Yapan</span>
                  <input
                    type="text"
                    name="surveyorName"
                    value={formData.surveyorName}
                    onChange={handleChange}
                    placeholder="Ad Soyad"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </label>

                <label className="space-y-1">
                  <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Öngörülen Hacim (m³)</span>
                  <NumericFormat
                    value={formData.estimatedVolume}
                    onValueChange={({ value }, source) => {
                      if (source.source !== 'event') return;
                      setFormData((prev) => ({
                        ...prev,
                        estimatedVolume: value
                      }));
                    }}
                    inputMode="decimal"
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={[',', '.']}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    allowNegative={false}
                    placeholder="4,50"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </label>
              </div>

            </div>
          </section>

          <section className="rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm lg:col-span-2">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">FİYATLANDIRMA</p>
              <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">Fiyatlandırma ve Teklif</h2>
            </div>

            <div className="mt-5 space-y-5 border-t border-slate-100 pt-5">
              <div>
                <p className="font-bold text-slate-700">Fiyatlandırma Tipi</p>
                <div className="mt-2 inline-flex rounded-2xl bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => handlePricingTypeChange('PER_M3')}
                    className={`rounded-xl px-4 py-2 text-xs font-black transition-all ${
                      formData.pricingType === 'PER_M3'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    m³ Başına Birim Fiyat
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePricingTypeChange('FLAT_RATE')}
                    className={`rounded-xl px-4 py-2 text-xs font-black transition-all ${
                      formData.pricingType === 'FLAT_RATE'
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Toplam Götürü Fiyat
                  </button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-1">
                  <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Para Birimi</span>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  >
                    {PRICE_CURRENCIES.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1">
                  <span className="ml-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {formData.pricingType === 'PER_M3'
                      ? `m³ Başına Fiyat (${selectedCurrencySymbol})`
                      : `Toplam Fiyat (${selectedCurrencySymbol})`}
                  </span>
                  <NumericFormat
                    value={displayPrice}
                    onValueChange={({ floatValue }, source) => {
                      if (source.source !== 'event') return;
                      const nextValue = Number(floatValue || 0);
                      setFormData((prev) => ({
                        ...prev,
                        ...(prev.pricingType === 'PER_M3'
                          ? { unitPriceMinor: nextValue }
                          : { totalPriceMinor: nextValue })
                      }));
                    }}
                    inputMode="decimal"
                    thousandSeparator="."
                    decimalSeparator=","
                    allowedDecimalSeparators={[',', '.']}
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    placeholder="0,00"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
                  />
                </label>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-500">Öngörülen Toplam Teklif Fiyatı</p>
                <p className="mt-2 text-2xl font-black text-blue-700">{formatCurrency(totalOffer, formData.currency || 'TRY')}</p>
              </div>
            </div>
          </section>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <OperationAddressBlock
            title="Alım Adresi"
            description="Origin"
            value={formData.pickupAddress}
            onChange={handleAddressChange('pickupAddress')}
          />

          <OperationAddressBlock
            title="Teslimat Adresi"
            description="Destination"
            value={formData.deliveryAddress}
            onChange={handleAddressChange('deliveryAddress')}
          />

          <section className="rounded-[2rem] border border-slate-100 bg-slate-50/70 p-5 shadow-sm lg:col-span-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Kayıt</p>
            <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">Hazır Olduğunda Kaydedin</h2>
            <p className="mt-3 text-sm font-medium leading-6 text-slate-500">
              Müşteri seçimi opsiyoneldir. Kaydettiğinizde sistem operasyonu oluşturur ve detay sayfasına yönlendirir.
            </p>

            <div className="mt-6 rounded-[1.5rem] border border-dashed border-slate-200 bg-white p-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              İsterseniz müşteri seçim penceresinden hızlı müşteri de oluşturabilirsiniz.
            </div>

            <button
              type="submit"
              disabled={saving}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-4 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Truck size={18} />}
              Operasyonu Başlat
            </button>
          </section>
        </div>
      </form>

      <AccountSelectModal
        isOpen={isAccountSelectOpen}
        onClose={() => setIsAccountSelectOpen(false)}
        onSelect={handleAccountSelect}
        accountType="CUSTOMER"
        title="Müşteri Seçimi"
        enableQuickCreate
      />
    </div>
  );
};

export default OperationCreate;
